import React, { useEffect, useRef, useState } from 'react';
import { Button, Chip, CircularProgress, Grid, Paper, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import JString from '@easytech-international-sdn-bhd/jstring';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import SendIcon from '@material-ui/icons/Send';
import ImageIcon from '@material-ui/icons/Image';
import VideocamIcon from '@material-ui/icons/Videocam';
import DescriptionIcon from '@material-ui/icons/Description';
import LinkIcon from '@material-ui/icons/Link';
import API_Service, { axiosDefaultConfig } from '../../../Services/API_Service';
import { req_announcement_clients, req_announcement_save, req_announcement_users } from '../../../Helper/Constants';
import { arrayBufferToBase64 } from '../../../Helper/Helper';
import VirtualListCBGroup from '../../../Components/VirtualListCBGroup';
import { buildAnnouncementFormData, getVideoLinkInfo, getClientDisplayName, filterAttachmentFiles, MAX_ATTACHMENTS } from '../announcementUtils';

const PRIORITIES = ['info', 'important', 'critical'];
const ACCEPT_TYPES = {
	image: ['image/jpeg', 'image/png', 'image/jpg'],
	video: ['video/mp4'],
	pdf: ['application/pdf']
};

const useStyles = makeStyles((theme) => ({
	formPaper: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		borderRadius: 10
	},
	sectionLabel: {
		fontSize: 11,
		fontWeight: 500,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1)
	},
	fieldGap: {
		marginTop: theme.spacing(2)
	},
	attachBtnRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(1)
	},
	attachBtn: {
		textTransform: 'none',
		fontSize: 12,
		border: `1px dashed ${theme.palette.divider}`,
		color: theme.palette.text.secondary
	},
	hiddenInput: {
		display: 'none'
	},
	chipRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2)
	},
	chip: {
		maxWidth: '100%'
	},
	youtubeRow: {
		display: 'flex',
		alignItems: 'center',
		marginTop: theme.spacing(2)
	},
	youtubeAddBtn: {
		marginLeft: theme.spacing(1),
		height: 40
	},
	audienceBtn: {
		width: '100%',
		height: 40,
		justifyContent: 'flex-start',
		textTransform: 'none'
	},
	audienceBtnText: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		width: '100%',
		textAlign: 'left'
	},
	recipientsRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'baseline',
		marginTop: theme.spacing(2),
		paddingTop: theme.spacing(1),
		borderTop: `1px solid ${theme.palette.divider}`
	},
	recipientsCount: {
		fontSize: 26,
		fontWeight: 300,
		color: '#FEF200'
	},
	priorityRow: {
		display: 'flex',
		gap: theme.spacing(1)
	},
	priorityBtn: {
		flex: 1,
		textTransform: 'none'
	},
	priorityInfoSel: {
		backgroundColor: '#9e9e9e',
		color: '#fff',
		'&:hover': { backgroundColor: '#8a8a8a' }
	},
	priorityInfoOut: {
		color: '#757575',
		borderColor: '#bdbdbd'
	},
	priorityImportantSel: {
		backgroundColor: '#FEF200',
		color: '#000',
		'&:hover': { backgroundColor: '#e6da00' }
	},
	priorityImportantOut: {
		color: '#c9b800',
		borderColor: '#FEF200'
	},
	priorityCriticalSel: {
		backgroundColor: '#FF5E5B',
		color: '#fff',
		'&:hover': { backgroundColor: '#e5504d' }
	},
	priorityCriticalOut: {
		color: '#FF5E5B',
		borderColor: '#FF5E5B'
	},
	saveDraftBtn: {
		color: '#fff',
		backgroundColor: '#616161',
		border: 'none',
		'&:hover': { backgroundColor: '#4e4e4e' },
		'&.Mui-disabled': {
			color: 'rgba(255, 255, 255, 0.7)',
			backgroundColor: '#cfcfcf',
			border: 'none'
		}
	},
	dateInput: {
		width: '100%'
	},
	hintText: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(1)
	},
	actionRow: {
		display: 'flex',
		gap: theme.spacing(1)
	},
	actionBtn: {
		flex: 1,
		height: 42,
		fontWeight: 600,
		textTransform: 'none'
	}
}));

export default function ComposeTab({ language, client, editData, onDone }) {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();

	const [title, setTitle] = useState('');
	const [content, setContent] = useState('');
	const [priority, setPriority] = useState('info');
	const [clientList, setClientList] = useState([]);
	const [selectedClients, setSelectedClients] = useState([]);
	const [clientView, setClientView] = useState(false);
	const [userList, setUserList] = useState([]);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [publishDate, setPublishDate] = useState(null);
	const [pendingFiles, setPendingFiles] = useState([]);
	const [keepAttachments, setKeepAttachments] = useState([]);
	const [youtubeLinks, setYoutubeLinks] = useState([]);
	const [youtubeInput, setYoutubeInput] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [youtubeOpen, setYoutubeOpen] = useState(false);
	const fileInputRef = useRef(null);

	const snack = (msg, type = 'success') => {
		enqueueSnackbar(msg, {
			variant: type,
			preventDuplicate: true,
			autoHideDuration: type === 'success' ? 2000 : 3000
		});
	};

	useEffect(() => {
		if (client) {
			loadClients();
			loadUsers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client]);

	useEffect(() => {
		if (editData) {
			setTitle(editData.title || '');
			setContent(editData.content || '');
			setPriority(editData.priority || 'info');
			setPublishDate(editData.publish_date ? new Date(editData.publish_date) : null);
			var kept = [];
			try {
				kept = JSON.parse(editData.attachment || '[]') || [];
			} catch (e) {
				kept = [];
			}
			setKeepAttachments(kept.filter(a => a.type !== 'youtube'));
			setYoutubeLinks(kept.filter(a => a.type === 'youtube').map(a => a.url));
			if (editData.audience_type === 'targeted' && editData.clients) {
				setSelectedClients(editData.clients.map(c => ({ name: getClientDisplayName(c), value: c })));
			} else {
				setSelectedClients([]);
			}
			if (editData.users && editData.users.length > 0) {
				setSelectedUsers(editData.users.map(u => ({ name: u.username, value: `${u.client}|${u.username}` })));
			} else {
				setSelectedUsers([]);
			}
			setPendingFiles([]);
		}
	}, [editData]);

	const loadClients = () => {
		API_Service.get(req_announcement_clients, axiosDefaultConfig(client)).then(({ data }) => {
			if (parseInt(data.result) === 1) {
				setClientList(data.extraData || []);
			} else {
				snack(data.message || 'Error', 'error');
			}
		}).catch(() => snack('Network error', 'error'));
	};

	const loadUsers = () => {
		API_Service.get(req_announcement_users, axiosDefaultConfig(client)).then(({ data }) => {
			if (parseInt(data.result) === 1) {
				setUserList(data.extraData || []);
			} else {
				snack(data.message || 'Error', 'error');
			}
		}).catch(() => snack('Network error', 'error'));
	};

	const clientOptions = clientList.map(c => ({
		name: getClientDisplayName(c.client_company),
		secondary: `${c.user_count} ${language.announcement_users}`,
		value: c.client_company,
		children: userList
			.filter(u => u.client_company === c.client_company)
			.map(u => ({ name: u.username, value: `${c.client_company}|${u.username}` }))
	}));

	const isAllClients = selectedClients.length === 0 || selectedClients.length === clientList.length;
	const scopeClients = isAllClients ? clientList.map(c => c.client_company) : selectedClients.map(c => c.value);
	const recipientCount = scopeClients.reduce((sum, code) => {
		const picked = selectedUsers.filter(u => String(u.value).split('|')[0] === code);
		if (picked.length > 0) {
			return sum + picked.length;
		}
		const found = clientList.find(c => c.client_company === code);
		return sum + (found ? parseInt(found.user_count) || 0 : 0);
	}, 0);
	const attachmentCount = pendingFiles.length + keepAttachments.length + youtubeLinks.length;
	const canSubmit = JString(title).isValid();

	const handleClientClose = (data) => {
		setClientView(false);
		if (data && data.clients) {
			setSelectedClients(data.clients);
			setSelectedUsers(data.users || []);
		} else if (data && data.value === '') {
			setSelectedClients([]);
			setSelectedUsers([]);
		}
	};

	const addYoutubeLink = () => {
		const url = youtubeInput.trim();
		if (!url) return;
		if (!getVideoLinkInfo(url)) {
			snack(language.announcement_youtube_invalid, 'warning');
			return;
		}
		if (attachmentCount >= MAX_ATTACHMENTS) {
			snack(language.announcement_too_many_files, 'warning');
			return;
		}
		setYoutubeLinks([...youtubeLinks, url]);
		setYoutubeInput('');
		setYoutubeOpen(false);
	};

	const openPicker = (type) => {
		if (fileInputRef.current) {
			fileInputRef.current.setAttribute('accept', ACCEPT_TYPES[type].join(','));
			fileInputRef.current.dataset.type = type;
			fileInputRef.current.click();
		}
	};

	const handleFilesPicked = (e) => {
		const type = e.target.dataset.type || 'image';
		const picked = Array.from(e.target.files || []);
		const result = filterAttachmentFiles(picked, ACCEPT_TYPES[type], attachmentCount);
		if (result.accepted.length > 0) {
			setPendingFiles(prev => [...prev, ...result.accepted]);
		}
		if (result.oversize) snack(language.announcement_file_too_large, 'warning');
		if (result.overflow) snack(language.announcement_too_many_files, 'warning');
		if (result.badType) snack(language.announcement_file_type_invalid, 'warning');
		e.target.value = '';
	};

	const attachIcon = (mimeOrType) => {
		if (mimeOrType.indexOf('image') === 0) return <ImageIcon fontSize="small" />;
		if (mimeOrType.indexOf('video') === 0) return <VideocamIcon fontSize="small" />;
		return <DescriptionIcon fontSize="small" />;
	};

	const priorityLabel = (p) => {
		if (p === 'important') return language.announcement_priority_important;
		if (p === 'critical') return language.announcement_priority_critical;
		return language.announcement_priority_info;
	};

	const priorityBtnClass = (p, selected) => {
		if (p === 'critical') return selected ? classes.priorityCriticalSel : classes.priorityCriticalOut;
		if (p === 'important') return selected ? classes.priorityImportantSel : classes.priorityImportantOut;
		return selected ? classes.priorityInfoSel : classes.priorityInfoOut;
	};

	const readFiles = (files) => Promise.all(files.map((file) => new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(new Error('File reading has failed'));
		reader.onload = () => resolve({
			file: arrayBufferToBase64(reader.result),
			name: file.name,
			type: file.type
		});
		reader.readAsArrayBuffer(file);
	})));

	const resetForm = () => {
		setTitle('');
		setContent('');
		setPriority('info');
		setSelectedClients([]);
		setSelectedUsers([]);
		setPublishDate(null);
		setPendingFiles([]);
		setKeepAttachments([]);
		setYoutubeLinks([]);
		setYoutubeInput('');
		setYoutubeOpen(false);
	};

	const handleSubmit = (action) => {
		if (!canSubmit) return;
		setSubmitting(true);
		readFiles(pendingFiles).then((fileArray) => {
			const formdata = buildAnnouncementFormData({
				id: editData ? editData.id : null,
				title,
				content,
				priority,
				audienceType: isAllClients ? 'all' : 'targeted',
				clients: selectedClients.map(c => c.value),
				users: selectedUsers.map(u => {
					const parts = String(u.value).split('|');
					return { client: parts[0], username: parts.slice(1).join('|') };
				}),
				publishDate: publishDate ? moment(publishDate).format('YYYY-MM-DD') : '',
				action,
				files: fileArray,
				keepAttachments,
				youtubeLinks
			});
			return API_Service.post(req_announcement_save, formdata, axiosDefaultConfig(client));
		}).then(({ data }) => {
			if (parseInt(data.result) === 1) {
				const failed = (data.extraData && data.extraData.failed_files) || [];
				snack(data.message, failed.length > 0 ? 'warning' : 'success');
				resetForm();
				onDone();
			} else {
				snack(data.message || 'Error', 'error');
			}
			setSubmitting(false);
		}).catch(() => {
			snack('Network error', 'error');
			setSubmitting(false);
		});
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} md={8}>
				<Paper variant="outlined" className={classes.formPaper}>
					<TextField
						label={language.announcement_title}
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						fullWidth
					/>
					<TextField
						label={language.announcement_content}
						value={content}
						onChange={(e) => setContent(e.target.value)}
						multiline
						rows={8}
						variant="outlined"
						fullWidth
						className={classes.fieldGap}
					/>
				</Paper>
				<Paper variant="outlined" className={classes.formPaper}>
					<Typography className={classes.sectionLabel}>{language.announcement_attachments}</Typography>
					{(keepAttachments.length > 0 || pendingFiles.length > 0 || youtubeLinks.length > 0) && (
						<div className={classes.chipRow}>
							{keepAttachments.map((item, index) => (
								<Chip
									key={`keep-${String(index)}`}
									icon={attachIcon(item.type)}
									label={item.name}
									onDelete={() => setKeepAttachments(keepAttachments.filter((_, i) => i !== index))}
									className={classes.chip}
								/>
							))}
							{pendingFiles.map((file, index) => (
								<Chip
									key={`new-${String(index)}`}
									icon={attachIcon(file.type)}
									label={file.name}
									onDelete={() => setPendingFiles(pendingFiles.filter((_, i) => i !== index))}
									className={classes.chip}
								/>
							))}
							{youtubeLinks.map((url, index) => (
								<Chip
									key={`yt-${String(index)}`}
									icon={<LinkIcon fontSize="small" />}
									label={url}
									onDelete={() => setYoutubeLinks(youtubeLinks.filter((_, i) => i !== index))}
									className={classes.chip}
								/>
							))}
						</div>
					)}
					<div className={classes.attachBtnRow}>
						<Button size="small" className={classes.attachBtn} startIcon={<DescriptionIcon />} onClick={() => openPicker('pdf')}>
							{language.announcement_add_pdf}
						</Button>
						<Button size="small" className={classes.attachBtn} startIcon={<ImageIcon />} onClick={() => openPicker('image')}>
							{language.announcement_add_image}
						</Button>
						<Button size="small" className={classes.attachBtn} startIcon={<VideocamIcon />} onClick={() => openPicker('video')}>
							{language.announcement_add_video}
						</Button>
						<Button size="small" className={classes.attachBtn} startIcon={<LinkIcon />} onClick={() => setYoutubeOpen(!youtubeOpen)}>
							{language.announcement_add_youtube}
						</Button>
					</div>
					<input type="file" multiple ref={fileInputRef} className={classes.hiddenInput} onChange={handleFilesPicked} />
					{youtubeOpen && (
						<div className={classes.youtubeRow}>
							<TextField
								label={language.announcement_youtube_url}
								value={youtubeInput}
								onChange={(e) => setYoutubeInput(e.target.value)}
								fullWidth
								size="small"
								variant="outlined"
							/>
							<Button
								variant="outlined"
								size="small"
								className={classes.youtubeAddBtn}
								onClick={addYoutubeLink}>
								{language.announcement_add}
							</Button>
						</div>
					)}
				</Paper>
			</Grid>
			<Grid item xs={12} md={4}>
				<Paper variant="outlined" className={classes.formPaper}>
					<Typography className={classes.sectionLabel}>{language.announcement_audience}</Typography>
					<Button
						className={classes.audienceBtn}
						variant="outlined"
						size="small"
						onClick={() => setClientView(true)}
						endIcon={<KeyboardArrowDownRoundedIcon />}>
						<Typography className={classes.audienceBtnText}>
							{isAllClients ? language.announcement_all_clients : `${selectedClients.length} ${language.selected}`}
						</Typography>
					</Button>
					<div className={classes.recipientsRow}>
						<Typography className={classes.sectionLabel}>{language.announcement_recipients}</Typography>
						<Typography className={classes.recipientsCount}>{recipientCount}</Typography>
					</div>
				</Paper>
				<Paper variant="outlined" className={classes.formPaper}>
					<Typography className={classes.sectionLabel}>{language.announcement_priority}</Typography>
					<div className={classes.priorityRow}>
						{PRIORITIES.map((p) => (
							<Button
								key={p}
								size="small"
								variant={priority === p ? 'contained' : 'outlined'}
								className={`${classes.priorityBtn} ${priorityBtnClass(p, priority === p)}`}
								onClick={() => setPriority(p)}>
								{priorityLabel(p)}
							</Button>
						))}
					</div>
				</Paper>
				<Paper variant="outlined" className={classes.formPaper}>
					<Typography className={classes.sectionLabel}>{language.announcement_publish_date}</Typography>
					<MuiPickersUtilsProvider utils={DateFnsUtils}>
						<DatePicker
							className={classes.dateInput}
							inputVariant="outlined"
							margin="dense"
							format="yyyy-MM-dd"
							value={publishDate}
							onChange={setPublishDate}
							clearable
						/>
					</MuiPickersUtilsProvider>
					<Typography className={classes.hintText}>{language.announcement_publish_date_hint}</Typography>
				</Paper>
				<div className={classes.actionRow}>
					<Button
						variant="outlined"
						className={`${classes.actionBtn} ${classes.saveDraftBtn}`}
						disabled={!canSubmit || submitting}
						onClick={() => handleSubmit('draft')}>
						{language.announcement_save_draft}
					</Button>
					<Button
						variant="contained"
						color="primary"
						className={classes.actionBtn}
						disabled={!canSubmit || submitting}
						endIcon={submitting ? <CircularProgress size={18} /> : <SendIcon />}
						onClick={() => handleSubmit('publish')}>
						{language.announcement_publish}
					</Button>
				</div>
			</Grid>

			<VirtualListCBGroup
				open={clientView}
				setClose={handleClientClose}
				title={language.announcement_audience}
				data={clientOptions}
				language={language}
				selectedClients={selectedClients}
				selectedUsers={selectedUsers}
			/>
		</Grid>
	);
}
